import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { documentApi } from '../api';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

export function PdfViewerPage() {
  const { juniperId } = useParams<{ juniperId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') as 'invoice' | 'ledger';
  const isTrr = searchParams.get('isTrr') === 'true';

  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    const fetchPdf = async () => {
      if (!juniperId || !type) {
        setError('Missing juniper ID or document type');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch signed URL for existing stored PDF
        if (isTrr) {
          // For TRR transactions, use the TRR download API
          const api = (await import('@xtnd/web/shared/api')).api;
          const response = await api.get<{ downloadUrl: string; filename: string }>(
            `/juniper-trr-transactions/${juniperId}/download/pdf`
          );
          setPdfUrl(response.downloadUrl);
        } else {
          // For regular Juniper transactions, use the existing API
          const response = await documentApi.getSignedUrl(juniperId!, type);
          setPdfUrl(response.signedUrl);
        }
      } catch (err) {
        console.error('Error fetching PDF:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        showToast.error(`Failed to load PDF: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();
  }, [juniperId, type, isTrr]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-xtnd-dark-50 dark:bg-xtnd-dark-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-xtnd-blue mx-auto mb-4"></div>
          <p className="text-xtnd-dark-500 dark:text-xtnd-dark-300">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-xtnd-dark-50 dark:bg-xtnd-dark-800 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <Icon name="HiExclamationCircle" className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              PDF Load Error
            </h2>
            <p className="text-red-700 dark:text-red-300 mb-4">
              {error}
            </p>
            <button
              onClick={handleBack}
              className="bg-xtnd-blue text-white px-4 py-2 rounded-md hover:bg-xtnd-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-xtnd-dark-50 dark:bg-xtnd-dark-800">
      {/* Header */}
      <div className="bg-white dark:bg-xtnd-dark-700 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center text-xtnd-dark-500 dark:text-xtnd-dark-300 hover:text-xtnd-dark-800 dark:hover:text-xtnd-dark-50 transition-colors"
            >
              <Icon name="HiArrowLeft" className="h-5 w-5 mr-2" />
              Back
            </button>
            <div>
              <h1 className="text-lg font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50">
                {type === 'invoice' ? 'Invoice' : 'Ledger'} PDF
              </h1>
              <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                {isTrr ? 'Trust Request' : 'Transaction'}: {juniperId}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 h-[calc(100vh-73px)]">
        {pdfUrl && (
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              fileUrl={pdfUrl}
              plugins={[defaultLayoutPluginInstance]}
              onDocumentLoad={() => {
                console.log('PDF document loaded successfully');
              }}
            />
          </Worker>
        )}
      </div>
    </div>
  );
}
